import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import Grid from "@mui/material/Grid";
import { fetchStudentAttendance } from "@/api/students";
import { QueryState } from "@/components/common/AsyncState";
import { KpiCard } from "@/components/common/KpiCard";
import { StatusChip } from "@/components/common/StatusChip";
import { ATTENDANCE_MARKABLE, ATTENDANCE_STATUS_LABELS, isPresentStatus, type AttendanceStatus } from "@/types/enums";
import { formatDate, formatPercent } from "@/utils/format";
import type { StudentAttendanceRecord } from "@/api/students";

const STATUS_HEAT: Record<AttendanceStatus, string> = {
  PRESENT: "#12876B",
  LEFT: "#8A7F86",
  EXCUSED: "#C77A0A",
  UNEXCUSED: "#C8253F",
  CANCELED: "#E9EEF2",
};

const DAY_LABELS = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];

export function StudentAttendanceTab({ studentId }: { studentId: string }) {
  const { data: records, isLoading, error } = useQuery({
    queryKey: ["student-attendance", studentId],
    queryFn: () => fetchStudentAttendance(studentId),
  });

  const stats = useMemo(() => computeStats(records ?? []), [records]);
  const heatmap = useMemo(() => buildDayHeatmap(records ?? []), [records]);

  return (
    <QueryState isLoading={isLoading} error={error}>
      <Stack spacing={3}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <KpiCard label="Посещаемость" value={formatPercent(stats.percent)} hint={`${stats.present} из ${stats.total} уроков`} color="success.main" />
          </Grid>
          <Grid item xs={12} sm={8}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {stats.breakdown.map((row) => (
                <StatusChip key={row.status} label={`${ATTENDANCE_STATUS_LABELS[row.status]}: ${row.count}`} />
              ))}
              {stats.breakdown.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  Записей о посещаемости пока нет.
                </Typography>
              )}
            </Stack>
          </Grid>
        </Grid>

        <Box>
          <Typography variant="subtitle2" mb={1}>
            Календарь посещаемости · 12 недель
          </Typography>
          <Stack direction="row" spacing={0.75} alignItems="flex-start">
            <Stack spacing={0.75} pt={2.5}>
              {DAY_LABELS.map((label) => (
                <Typography key={label} variant="caption" color="text.secondary" sx={{ height: 14, lineHeight: "14px", fontSize: 10 }}>
                  {label}
                </Typography>
              ))}
            </Stack>
            <Box display="grid" gridTemplateColumns="repeat(12, 1fr)" gap={0.75} flex={1}>
              {heatmap.weeks.map((week) => (
                <Stack key={week.key} spacing={0.75}>
                  <Typography variant="caption" color="text.secondary" textAlign="center" fontSize={10}>
                    {week.label}
                  </Typography>
                  {week.days.map((day) => (
                    <Tooltip
                      key={day.date}
                      title={
                        day.status
                          ? `${formatDate(day.date)}: ${ATTENDANCE_STATUS_LABELS[day.status]}`
                          : `${formatDate(day.date)}: нет урока`
                      }
                    >
                      <Box
                        sx={{
                          width: "100%",
                          height: 14,
                          borderRadius: 0.5,
                          bgcolor: day.status ? STATUS_HEAT[day.status] : "#eef1f4",
                          border: "1px solid",
                          borderColor: day.status ? "transparent" : "divider",
                        }}
                      />
                    </Tooltip>
                  ))}
                </Stack>
              ))}
            </Box>
          </Stack>
        </Box>
      </Stack>
    </QueryState>
  );
}

function computeStats(records: StudentAttendanceRecord[]) {
  const active = records.filter((record) => record.status !== "CANCELED" && !record.lesson?.canceled);
  const present = active.filter((record) => isPresentStatus(record.status)).length;
  const counts = new Map<AttendanceStatus, number>();

  for (const record of active) {
    counts.set(record.status, (counts.get(record.status) ?? 0) + 1);
  }

  const breakdown = ATTENDANCE_MARKABLE
    .filter((status) => (counts.get(status) ?? 0) > 0)
    .map((status) => ({ status, count: counts.get(status) ?? 0 }));

  return {
    total: active.length,
    present,
    percent: active.length ? Math.round((present / active.length) * 100) : 0,
    breakdown,
  };
}

function buildDayHeatmap(records: StudentAttendanceRecord[]) {
  const byDate = new Map<string, AttendanceStatus>();
  for (const record of records) {
    const dateStr = record.lesson?.date?.slice(0, 10);
    if (!dateStr || record.status === "CANCELED" || record.lesson?.canceled) continue;
    byDate.set(dateStr, record.status);
  }

  const end = new Date();
  end.setHours(0, 0, 0, 0);
  const start = new Date(end);
  start.setDate(start.getDate() - 83);

  const weeks: { key: string; label: string; days: { date: string; status: AttendanceStatus | null }[] }[] = [];
  for (let weekIndex = 0; weekIndex < 12; weekIndex += 1) {
    const weekStart = new Date(start);
    weekStart.setDate(start.getDate() + weekIndex * 7);
    const days: { date: string; status: AttendanceStatus | null }[] = [];

    for (let dayIndex = 0; dayIndex < 7; dayIndex += 1) {
      const day = new Date(weekStart);
      day.setDate(weekStart.getDate() + dayIndex);
      const key = day.toISOString().slice(0, 10);
      days.push({ date: key, status: byDate.get(key) ?? null });
    }

    weeks.push({
      key: weekStart.toISOString().slice(0, 10),
      label: weekStart.toLocaleDateString("ru-RU", { day: "numeric", month: "short" }),
      days,
    });
  }

  return { weeks };
}
